import { Injectable } from '@angular/core';
import { CheckoutSessionEntity } from './checkout-session.entity';
import { genId } from 'src/core/utils';
import { MessageService } from '../core-services/message.service';
import { TokenSubscriptionTrait } from './subscription.entity';
// import { AuthService } from '../core-services/auth.service';
import { Subscription } from 'rxjs';
import { CoreService } from '../core-services/core.service';
import { AnalyticsService } from '../core-services/analytics.service';
import { StorageService } from '../core-services/storage.service';
import { PageRoutes } from '../app-routing.module';
import { getFunctions, httpsCallable } from 'firebase/functions'
import { ObservableService } from '../core-services/observable.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';

// const defaultSubscriptionPriceId = 'price_1NNSRfI181a1rkXSenEQnsw9';
// const defaultSubscriptionPriceId = 'price_1OTUC0I181a1rkXSJckUKYXL';
// const defaultSubscriptionProductId = 'prod_PI4KJJ6wQgMWvo';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  private _current: TokenSubscriptionTrait;

  public alreadyTrial: boolean = false;

  public get current() {
    return this._current;
  }

  private authsub: Subscription;
  private onSubscriptionNeededSub: Subscription;
  private _isOpen: boolean = false;
  public get isOpen() {
    return this._isOpen;
  }
  public freeplan: TokenSubscriptionTrait;
  public basicplan: TokenSubscriptionTrait;

  private loading: boolean = false;
  public get isLoading() {
    return this.loading;
  }

  public get defaultSubscriptionProductId() {
    return environment.subscription.planId;
  }

  constructor(
    private message: MessageService,
    private core: CoreService,
    private analytics: AnalyticsService,
    private observable: ObservableService,
    private translate: TranslateService,
    ) { 
      this.init();
    }

  public async init() {

    if (!this.onSubscriptionNeededSub) {
      this.onSubscriptionNeededSub = this.observable.onSubscriptionNeeded$.subscribe(async () => {
        this.showModal();
      });
    }

    this.freeplan = {
      id: null,
      updatedAt: null,
      version: null,
      createdAt: null,
      period_start: null,
      period_end: null,
      cancel_at_end: null,
      status: !['active', 'trialing'].includes(this.current?.status) ? 'active' : null,
      trial_start: null,
      trial_end: null,
      stripelink: null,
      plan: {
        id: null,
        currency: null,
        amount: 0,
        interval: null,
        interval_count: null,
        name: await this.translate.get('SUBSCRIPTION.plan.free.name').toPromise(),
        description: await this.translate.get('SUBSCRIPTION.plan.free.description').toPromise()
      },
      data: {
        maxClients: 4,
        maxAppointments: 8,
        currentClients: this.current?.data?.currentClients || 0,
        currentAppointments: this.current?.data?.currentAppointments || 0,
        maxWhatsappNotifications: 0,
        currentWhatsappNotifications: 0
      }
    };
    
    this.basicplan = {
      id: this.current?.id,
      updatedAt: this.current?.updatedAt,
      version: this.current?.version,
      createdAt: this.current?.createdAt,
      period_start: this.current?.period_start,
      period_end: this.current?.period_end,
      cancel_at_end: this.current?.cancel_at_end,
      status: this.current?.status,
      trial_start: this.current?.trial_start,
      trial_end: this.current?.trial_end,
      stripelink: this.current?.stripelink,
      paymentMethod: this.current?.paymentMethod,
      plan: {
        id: this.current?.plan?.id,
        currency: this.current?.plan?.currency,
        amount: this.current?.plan?.amount || 2500,
        interval: this.current?.plan?.interval,
        interval_count: this.current?.plan?.interval_count,
        name: this.current?.plan?.name ||  await this.translate.get('SUBSCRIPTION.plan.basic.name').toPromise(),
        description:  await this.translate.get('SUBSCRIPTION.plan.basic.description').toPromise(),
        canTrial: !this.alreadyTrial
      },
      data: {
        maxClients: -1,
        maxAppointments: -1,
        currentClients: this.current?.data?.currentClients || 0,
        currentAppointments: this.current?.data?.currentAppointments || 0,
        maxWhatsappNotifications: this.current?.data?.maxWhatsappNotifications || 0,
        currentWhatsappNotifications: this.current?.data?.currentWhatsappNotifications || 0
      }      
    };

  }

  public async subscribe(productId: string = this.defaultSubscriptionProductId) {
    this.loading = true;
    try {
      return await new Promise(async (resolve) => {
        // this.core.loader = true;
        const id = genId();
        const url = `${window.location.origin}/${PageRoutes.DASHBOARD}/${PageRoutes.SUBSCRIPTION}/${id}`;
        const checkoutSession = new CheckoutSessionEntity(
          id, 
          productId, 
          `${url}/success`,
          `${url}`,
          {
            teste: true,
            id: genId()
          }
        );
        const timeout = setTimeout(resolve, 20000);
        await checkoutSession.persist((snap) => {
          
          const { error, url } = snap.data();
          // this.core.loader = false;
          if (error) {
            // Show an error to your customer and
            // inspect your Cloud Function logs in the Firebase console.
            console.log(`An error occured: ${error.message}`);
            this.message.showToast(`An error occured: ${error.message}`);
            clearTimeout(timeout);
            resolve(false);
          }

          if (url) {
            this.analytics.subscribe(checkoutSession.export());
            // We have a Stripe Checkout URL, let's redirect.
            window.location.assign(url);
            clearTimeout(timeout);
            setTimeout(resolve, 10000);
          } 
        })
      });
   } finally {
    this.loading = false;
   }
  }

  // public async getAndUpdateCurrent() {
  //   const token = await this.auth.currentUser?.getIdTokenResult(true)
  //   if (token) {
  //     this.updateCurrent(token.claims.subscription);
  //   }
  // }

  public updateCurrent(subscription: TokenSubscriptionTrait) {
    this._current = !subscription ? null : {
      ...subscription,
      createdAt: (subscription?.createdAt || 0) * 1000,
      period_start: (subscription?.period_start || 0) * 1000,
      period_end: (subscription?.period_end || 0) * 1000,
      trial_start: (subscription?.trial_start || 0) * 1000,
      trial_end: (subscription?.trial_end || 0) * 1000,
    };
  }

  public async cancel() {
    if (await this.message.confirm('SUBSCRIPTION.cancel_confirm')) {
      try {
        console.log("cancelling subscription")
        const functions = getFunctions();
        const deleteCancelSubscription = httpsCallable(functions, 'cancelSubscription');
        const data = await deleteCancelSubscription() as any;
        console.log(data);
        if (data.data.passed) {
          this.observable.publishOnCheckout();
          // data.data?.cancelAt
          this.message.showToast('SUBSCRIPTION.cancel_success');
          return true;
        } else {
          this.message.showToast('SUBSCRIPTION.cancel_error');
          return false;
        }
      } catch(e) {
        console.log(e);
        this.message.showToast('SUBSCRIPTION.cancel_error');
        return false;
      }
    }
  }


  public async edit() {
      try {
        console.log("edit subscription")
        const functions = getFunctions();
        const editSubscription = httpsCallable(functions, 'editSubscription');
        const return_url = `${window.location.origin}/${PageRoutes.DASHBOARD}/${PageRoutes.SUBSCRIPTION}`;
        const data = await editSubscription({
          return_url,
        }) as any;
        console.log(data);
        if (data.data.passed && data.data.data.url) {
            // this.analytics.subscribe(checkoutSession.export());
            // We have a Stripe Checkout URL, let's redirect.
            window.location.assign(data.data.data.url);


          return true;
        } else {
          this.message.showToast('SUBSCRIPTION.cancel_error');
          return false;
        }
      } catch(e) {
        console.log(e);
        this.message.showToast('SUBSCRIPTION.cancel_error');
        return false;
      }
  }

  public showModal(show: boolean = true) {
    this._isOpen = show;
  }

}
